import React, { useState, useEffect } from 'react';
import { 
  Type, 
  Palette, 
  Layout, 
  Image as ImageIcon, 
  Phone, 
  Mail, 
  MapPin, 
  Check, 
  Save, 
  Upload, 
  Download, 
  Trash2, 
  Plus, 
  Sparkles, 
  Layers, 
  Eye, 
  Briefcase, 
  FileText, 
  Users, 
  HelpCircle,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  Handshake,
  Search,
  HardDrive,
  Globe,
  Link as LinkIcon,
  Video
} from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';
import { SiteContent } from '../../types/siteContent';
import { Apartment } from '../../types';
import { STOCK_GALLERY_IMAGES } from '../../data/initialData';
import { isVideoUrl, formatMediaUrl } from '../../utils/mediaUtils';
import SEOManager from './SEOManager';
import { MediaPickerModal } from './MediaPickerModal';

const FONT_OPTIONS_HEADINGS = [
  { id: 'Montserrat', name: 'Montserrat (Препоръчан за заглавия - Модерен & Елегантен)' },
  { id: 'Playfair Display', name: 'Playfair Display (Луксозен класически Serif)' },
  { id: 'Plus Jakarta Sans', name: 'Plus Jakarta Sans (Премиум геометричен Sans)' },
  { id: 'Inter', name: 'Inter (Технологичен, изчистен & високочетим)' },
  { id: 'Cormorant Garamond', name: 'Cormorant Garamond (Ексклузивен италиански стил Serif)' },
  { id: 'Cinzel', name: 'Cinzel (Класически римски монументален шрифт)' },
  { id: 'Rubik', name: 'Rubik (Мек и приятелски геометричен Sans)' },
];

const FONT_OPTIONS_BODY = [
  { id: 'Inter', name: 'Inter (Оптимална четимост за всякакъв екран)' },
  { id: 'Montserrat', name: 'Montserrat (Еднакъв луксозен почерк с хедъра)' },
  { id: 'Plus Jakarta Sans', name: 'Plus Jakarta Sans (Модерен архитектурен облик)' },
  { id: 'Rubik', name: 'Rubik (Лесен за четене на мобилни устройства)' },
];

const ALL_COMMON_VARNA_NEIGHBORHOODS = [
  'Център',
  'Бриз',
  'Чайка',
  'Левски',
  'Гръцка махала',
  'Колхозен пазар',
  'Възраждане',
  'Победа',
  'м-т Ален Мак',
  'Виница',
  'Трошево',
  'Цветен квартал',
  'Аспарухово',
  'Св. Св. Константин и Елена',
  'Златни пясъци',
  'ВИНС',
  'Младост',
  'Галата',
  'Владиславово',
  'м-т Траката',
  'м-т Евксиноград',
  'м-т Пчелина',
  'м-т Сотира'
];

const DEFAULT_NEIGHBORHOOD_IMAGES: Record<string, string> = {
  'Център': 'https://images.unsplash.com/photo-1577495508048-b635879837f1?w=600&q=80',
  'Бриз': 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80',
  'Чайка': 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80',
  'Гръцка махала': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80',
  'Левски': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80',
  'ВИНС': 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80',
  'Колхозен пазар': 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80',
  'Възраждане': 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&q=80',
  'Победа': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&q=80',
  'м-т Ален Мак': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
  'Виница': 'https://images.unsplash.com/photo-1512915922686-57c11dde9b6b?w=600&q=80',
  'Трошево': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80',
  'Цветен квартал': 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&q=80',
  'Аспарухово': 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&q=80',
  'Св. Св. Константин и Елена': 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=600&q=80',
  'Златни пясъци': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
};

interface SiteContentEditorProps {
  apartments?: Apartment[];
}

export default function SiteContentEditor({ apartments = [] }: SiteContentEditorProps) {
  const [liveProperties, setLiveProperties] = useState<Apartment[]>(apartments);

  useEffect(() => {
    if (apartments && apartments.length > 0) {
      setLiveProperties(apartments);
    } else {
      fetch('/api/properties')
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setLiveProperties(data);
        })
        .catch(() => {});
    }
  }, [apartments]);
  const { 
    content, 
    updateSection, 
    updateContent, 
    saveContent, 
    isSaving, 
    hasUnsavedChanges, 
    lastSavedAt 
  } = useSiteContent();

  const [activeTab, setActiveTab] = useState<
    'branding' | 'navigation' | 'hero' | 'featured' | 'services' | 'about' | 'team' | 'cta' | 'pages' | 'contacts' | 'locations' | 'footer' | 'seo' | 'backup'
  >('branding');

  // Popular Locations creator state (with dropdown from uploaded properties)
  const [newLocSelectedNeigh, setNewLocSelectedNeigh] = useState<string>('');
  const [newLocCustomNeigh, setNewLocCustomNeigh] = useState<string>('');
  const [newLocName, setNewLocName] = useState<string>('');
  const [newLocMode, setNewLocMode] = useState<'dropdown' | 'custom'>('dropdown');

  const [notification, setNotification] = useState<string | null>(null);
  const [mediaPicker, setMediaPicker] = useState<{
    isOpen: boolean;
    onSelect: (url: string) => void;
    allowedType?: 'image' | 'video' | 'all';
    title?: string;
  }>({
    isOpen: false,
    onSelect: () => {},
    allowedType: 'image',
    title: 'Изберете файл от public/'
  });

  // Gallery images for 1-click visual selection (Favicon, Logo, Hero, etc.)
  const [galleryImages, setGalleryImages] = useState<string[]>([
    '/imotencenterlogo-cropped.png',
    '/imotencenterlogo.png',
    ...STOCK_GALLERY_IMAGES
  ]);

  useEffect(() => {
    fetch('/media-manifest.json')
      .then(res => res.json())
      .then(files => {
        if (Array.isArray(files)) {
          const imgs = files
            .filter((f: any) => f.type === 'image' || ['png', 'jpg', 'jpeg', 'webp', 'svg', 'ico'].includes(f.extension))
            .map((f: any) => f.url);
          setGalleryImages(Array.from(new Set(['/imotencenterlogo-cropped.png', '/imotencenterlogo.png', ...imgs, ...STOCK_GALLERY_IMAGES])));
        }
      })
      .catch(() => {});
  }, []);

  // Helper to show temporary toast
  const showToast = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3500);
  };

  const handleSave = async () => {
    const success = await saveContent();
    if (success) {
      showToast('Всички промени бяха запазени успешно!');
    } else {
      showToast('Грешка при запазване в базата данни! Проверете интернет връзката и администраторските права.');
    }
  };

  // Generic File Uploader for images / SVGs
  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>, 
    onResult: (dataUrl: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          onResult(reader.result);
          showToast('Изображението беше качено успешно!');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // JSON Export
  const handleExportJSON = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(content, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `varna_imoti_content_backup_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    showToast('Конфигурацията беше изтеглена като JSON архив!');
  };

  // Popular Locations Manager with real-time property counts & dropdown of uploaded properties
  const renderPopularLocationsManager = () => {
    const list = (apartments && apartments.length > 0) ? apartments : liveProperties;
    const counts: Record<string, number> = {};
    (list || []).forEach(apt => {
      const n = (apt.neighborhood || '').trim();
      if (n) {
        counts[n] = (counts[n] || 0) + 1;
      }
    });

    const existingUploadedNeighs = Object.keys(counts).sort((a, b) => a.localeCompare(b, 'bg'));
    const currentTargetNeigh = (newLocMode === 'dropdown' ? newLocSelectedNeigh : newLocCustomNeigh).trim();
    const currentMatches = currentTargetNeigh ? (counts[currentTargetNeigh] || 0) : 0;

    return (
      <div className="space-y-6">
        <div className="border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#C5A46E]" />
            <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
              Управление на Популярни локации & Райони
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Тук настройвате кварталите и районите, които се показват като бутони и линкове на сайта (във футъра и секциите за популярни райони). Когато потребител кликне върху някоя от тези локации, офертите се филтрират автоматично по този квартал.
          </p>
        </div>

        {/* 1. Add New Location Card with Dropdown of Available Uploaded Neighborhoods */}
        <div className="bg-gradient-to-br from-amber-50/70 via-slate-50 to-white border border-[#C5A46E]/40 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex justify-between items-start gap-4">
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-[#8C6221] bg-[#C5A46E]/15 px-2 py-0.5 rounded">
                Свързване с налични обяви
              </span>
              <h4 className="text-sm font-black text-[#0F1E32] uppercase tracking-wide mt-1">
                Добави нова локация от качените имоти
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                Изберете квартал от качените оферти в системата чрез падащото меню или въведете нов район ръчно.
              </p>
            </div>
            
            <div className="flex bg-slate-200/80 p-0.5 rounded-lg text-xs font-bold shrink-0">
              <button
                type="button"
                onClick={() => setNewLocMode('dropdown')}
                className={`px-3 py-1 rounded-md transition cursor-pointer ${newLocMode === 'dropdown' ? 'bg-white text-[#0F1E32] shadow-sm' : 'text-slate-600 hover:text-black'}`}
              >
                Падащо меню ({existingUploadedNeighs.length})
              </button>
              <button
                type="button"
                onClick={() => setNewLocMode('custom')}
                className={`px-3 py-1 rounded-md transition cursor-pointer ${newLocMode === 'custom' ? 'bg-white text-[#0F1E32] shadow-sm' : 'text-slate-600 hover:text-black'}`}
              >
                Ръчно въвеждане
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 items-end">
            {/* Neighborhood Selector */}
            <div className="md:col-span-5">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {newLocMode === 'dropdown' ? 'Избери квартал от наличните обяви *' : 'Въведете име на квартал *'}
              </label>

              {newLocMode === 'dropdown' ? (
                <select
                  value={newLocSelectedNeigh}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setNewLocSelectedNeigh(selected);
                    if (selected && (!newLocName || newLocName.startsWith('Имоти в ') || newLocName === 'Имоти в нов район')) {
                      setNewLocName(`Имоти в ${selected}`);
                    }
                  }}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-[#C5A46E] focus:outline-none shadow-sm cursor-pointer"
                >
                  <option value="">-- Изберете квартал от наличните обяви ({existingUploadedNeighs.length}) --</option>
                  {existingUploadedNeighs.length > 0 && (
                    <optgroup label="✅ Квартали от качените имоти в системата">
                      {existingUploadedNeighs.map(neigh => (
                        <option key={neigh} value={neigh}>
                          {neigh} ({counts[neigh]} {counts[neigh] === 1 ? 'оферта' : 'оферти'})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="🏙️ Други популярни райони във Варна">
                    {ALL_COMMON_VARNA_NEIGHBORHOODS.filter(n => !existingUploadedNeighs.includes(n)).map(neigh => (
                      <option key={neigh} value={neigh}>
                        {neigh} (0 оферти в момента)
                      </option>
                    ))}
                  </optgroup>
                </select>
              ) : (
                <input
                  type="text"
                  value={newLocCustomNeigh}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewLocCustomNeigh(val);
                    if (!newLocName || newLocName.startsWith('Имоти в ') || newLocName === 'Имоти в нов район') {
                      setNewLocName(val ? `Имоти в ${val}` : '');
                    }
                  }}
                  placeholder="напр. Колхозен пазар, Бриз, Център..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-[#C5A46E] focus:outline-none shadow-sm"
                />
              )}
            </div>

            {/* Display Link Name */}
            <div className="md:col-span-4">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Име на бутона / линка *
              </label>
              <input
                type="text"
                value={newLocName}
                onChange={(e) => setNewLocName(e.target.value)}
                placeholder="напр. Имоти в Бриз"
                className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-[#C5A46E] focus:outline-none shadow-sm"
              />
            </div>

            {/* Add Button */}
            <div className="md:col-span-3">
              <button
                type="button"
                onClick={() => {
                  const targetNeigh = (newLocMode === 'dropdown' ? newLocSelectedNeigh : newLocCustomNeigh).trim();
                  if (!targetNeigh) {
                    showToast('Моля, изберете или въведете квартал!');
                    return;
                  }
                  const finalName = newLocName.trim() || `Имоти в ${targetNeigh}`;
                  const newLoc = {
                    id: `pl-${Date.now()}`,
                    name: finalName,
                    neighborhood: targetNeigh,
                    image: DEFAULT_NEIGHBORHOOD_IMAGES[targetNeigh] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80'
                  };
                  updateSection('footer', {
                    popularLocations: [...(content.footer.popularLocations || []), newLoc]
                  });
                  setNewLocSelectedNeigh('');
                  setNewLocCustomNeigh('');
                  setNewLocName('');
                  showToast(`Локацията „${finalName}“ (${targetNeigh}) беше добавена успешно!`);
                }}
                className="w-full bg-[#0F1E32] hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider py-2.5 px-4 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer h-[42px]"
              >
                <Plus className="w-4 h-4 text-[#C5A46E]" />
                Добави район
              </button>
            </div>
          </div>

          {/* Real-time Indicator banner */}
          {currentTargetNeigh && (
            <div className="flex items-center gap-2 bg-white/95 px-3 py-2 rounded-xl border border-slate-200 text-xs">
              <MapPin className="w-4 h-4 text-[#C5A46E] shrink-0" />
              <span className="text-slate-700">
                Избран квартал: <strong className="text-slate-900">{currentTargetNeigh}</strong>
                {currentMatches > 0 ? (
                  <span className="ml-2 font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    ✅ Налични {currentMatches} {currentMatches === 1 ? 'оферта' : 'оферти'} в системата
                  </span>
                ) : (
                  <span className="ml-2 font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                    ℹ️ В момента няма качени оферти, но кликът ще филтрира по този квартал
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* 2. Column Title Setting */}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
            Заглавие на секцията / колоната
          </label>
          <input
            type="text"
            value={content.footer.popularLocationsTitle}
            onChange={(e) => updateSection('footer', { popularLocationsTitle: e.target.value })}
            className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-slate-800"
            placeholder="ПОПУЛЯРНИ ЛОКАЦИИ"
          />
        </div>

        {/* 3. Existing Locations List */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C5A46E]" />
              Активни локации ({content.footer.popularLocations?.length || 0})
            </h4>
            <span className="text-[11px] text-slate-500">
              Подредете или редактирайте имената и свързаните квартали
            </span>
          </div>

          <div className="space-y-2.5">
            {(content.footer.popularLocations || []).map((loc, idx) => {
              const count = counts[loc.neighborhood] || 0;
              return (
                <div 
                  key={loc.id || idx} 
                  className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition"
                >
                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-[11px] shrink-0">
                    {idx + 1}
                  </div>

                  {/* Display Name */}
                  <div className="flex-1">
                    <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5 sm:hidden">
                      Име на линка
                    </label>
                    <input
                      type="text"
                      value={loc.name}
                      onChange={(e) => {
                        const updated = [...content.footer.popularLocations];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        updateSection('footer', { popularLocations: updated });
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:bg-white"
                      placeholder="Име на връзката (напр. Имоти в Бриз)"
                    />
                  </div>

                  {/* Neighborhood Selector with Uploaded Properties options */}
                  <div className="w-full sm:w-56">
                    <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5 sm:hidden">
                      Квартал за филтриране
                    </label>
                    <select
                      value={loc.neighborhood}
                      onChange={(e) => {
                        const updated = [...content.footer.popularLocations];
                        const newN = e.target.value;
                        updated[idx] = { 
                          ...updated[idx], 
                          neighborhood: newN,
                          image: DEFAULT_NEIGHBORHOOD_IMAGES[newN] || updated[idx].image
                        };
                        updateSection('footer', { popularLocations: updated });
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:bg-white cursor-pointer"
                    >
                      {/* Current selected option if not in list */}
                      {loc.neighborhood && !existingUploadedNeighs.includes(loc.neighborhood) && !ALL_COMMON_VARNA_NEIGHBORHOODS.includes(loc.neighborhood) && (
                        <option value={loc.neighborhood}>{loc.neighborhood} (текущ)</option>
                      )}
                      {existingUploadedNeighs.length > 0 && (
                        <optgroup label="✅ Квартали от качените имоти">
                          {existingUploadedNeighs.map(neigh => (
                            <option key={neigh} value={neigh}>
                              {neigh} ({counts[neigh]} {counts[neigh] === 1 ? 'оферта' : 'оферти'})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="🏙️ Други квартали на Варна">
                        {ALL_COMMON_VARNA_NEIGHBORHOODS.filter(n => !existingUploadedNeighs.includes(n)).map(neigh => (
                          <option key={neigh} value={neigh}>
                            {neigh} (0 оферти)
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Real-time Match Count Badge */}
                  <div className="shrink-0 flex items-center justify-between sm:justify-start gap-2">
                    <span 
                      className={`text-[11px] font-bold px-2 py-1 rounded-md border ${
                        count > 0 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}
                      title={count > 0 ? `${count} оферти съвпадат с този филтър` : 'Няма оферти в момента'}
                    >
                      {count > 0 ? `${count} ${count === 1 ? 'оферта' : 'оферти'}` : '0 оферти'}
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        const updated = content.footer.popularLocations.filter((_, i) => i !== idx);
                        updateSection('footer', { popularLocations: updated });
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition cursor-pointer"
                      title="Премахни локация"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            {(!content.footer.popularLocations || content.footer.popularLocations.length === 0) && (
              <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-xl text-xs text-slate-500">
                Няма добавени популярни локации. Използвайте формуляра по-горе, за да добавите първата си локация от качените оферти.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left font-sans">
      {/* Top action bar */}
      <div className="bg-slate-900 text-white px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A46E]" />
            <h2 className="text-lg font-black uppercase tracking-wide">
              CMS Редактор на Съдържанието & Дизайна
            </h2>
            {hasUnsavedChanges && (
              <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                Незапазени промени
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Управлявайте всички видими надписи, заглавия, бутони, снимки, шрифтове и футър секции на сайта в реално време.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {lastSavedAt && (
            <span className="text-[11px] text-slate-400 hidden md:inline">
              Последно запазване: {lastSavedAt}
            </span>
          )}
          <button
            id="cms-save-button"
            onClick={handleSave}
            disabled={isSaving}
            className={`px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition flex items-center gap-2 shadow-md cursor-pointer ${
              hasUnsavedChanges
                ? 'bg-[#C5A46E] hover:bg-[#B1905E] text-white animate-pulse'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Запазване...' : 'Запази промените'}
          </button>
        </div>
      </div>

      {/* Floating Notification Toast */}
      {notification && (
        <div className="bg-emerald-600 text-white px-4 py-2.5 text-xs font-bold text-center flex items-center justify-center gap-2 transition-all animate-fade-in shadow">
          <Check className="w-4 h-4" />
          <span>{notification}</span>
        </div>
      )}

      {/* Main CMS Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[650px]">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-3 bg-slate-50 border-r border-slate-200 p-3 space-y-1">
          {[
            { id: 'branding', label: '🎨 Шрифтове и Бранд', icon: Palette },
            { id: 'navigation', label: '🧭 Навигация и Хедър', icon: Layout },
            { id: 'hero', label: '🏠 Начална & Hero', icon: Sparkles },
            { id: 'featured', label: '🌟 Подбрани оферти', icon: Layers },
            { id: 'services', label: '🤝 Нашите услуги', icon: Handshake },
            { id: 'about', label: '📖 За нас & Мисия', icon: FileText },
            { id: 'team', label: '👥 Секция Екип', icon: Users },
            { id: 'cta', label: '📢 CTA Призив', icon: Phone },
            { id: 'pages', label: '🔑 Продажби и Наеми', icon: Briefcase },
            { id: 'contacts', label: '📍 Страница Контакти', icon: MapPin },
            { id: 'locations', label: '🗺️ Популярни локации', icon: MapPin },
            { id: 'footer', label: '🦶 Футър (Footer)', icon: Layout },
            { id: 'seo', label: '🔍 SEO Оптимизация', icon: Search },
            { id: 'backup', label: '💾 Архивиране (JSON)', icon: Download },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold tracking-wide transition cursor-pointer ${
                  isActive
                    ? 'bg-[#0F1E32] text-white shadow'
                    : 'text-slate-600 hover:bg-slate-200/70 hover:text-[#0F1E32]'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-[#C5A46E]' : 'text-slate-400'}`} />
                  <span>{tab.label}</span>
                </div>
                <ChevronRight className={`w-3.5 h-3.5 ${isActive ? 'text-[#C5A46E]' : 'text-slate-300'}`} />
              </button>
            );
          })}
        </div>

        {/* Content Configuration Panels */}
        <div className="lg:col-span-9 p-6 md:p-8 overflow-y-auto max-h-[800px]">
          
          {/* ================= 1. BRANDING & FONTS ================= */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Шрифтове, Бранд и Лого
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Задайте шрифтовите фамилии на сайта, наименованието на агенцията, слогана и логото.
                </p>
              </div>

              {/* Typography */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                  <Type className="w-4 h-4 text-[#C5A46E]" />
                  Шрифтова йерархия (Google Fonts с пълна поддръжка на кирилица)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">
                      Шрифт за Заглавия (h1, h2, h3, h4)
                    </label>
                    <select
                      value={content.branding.headingFont}
                      onChange={(e) => updateSection('branding', { headingFont: e.target.value as any })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
                    >
                      {FONT_OPTIONS_HEADINGS.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Пример за текущо заглавие: <span style={{ fontFamily: content.branding.headingFont }} className="font-bold text-[#0F1E32]">Варна Имотен Център — 2026</span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">
                      Шрифт за Основен текст (Тяло, параграфи, бутони)
                    </label>
                    <select
                      value={content.branding.bodyFont}
                      onChange={(e) => updateSection('branding', { bodyFont: e.target.value as any })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
                    >
                      {FONT_OPTIONS_BODY.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Пример за текст: <span style={{ fontFamily: content.branding.bodyFont }} className="text-slate-600">Професионални консултации и сделки с имоти.</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Title & Slogan */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Име на фирмата / Бранд (Заглавие на сайта)
                  </label>
                  <input
                    type="text"
                    value={content.branding.siteTitle}
                    onChange={(e) => updateSection('branding', { siteTitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Слоган / Подзаглавие
                  </label>
                  <input
                    type="text"
                    value={content.branding.siteSlogan}
                    onChange={(e) => updateSection('branding', { siteSlogan: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
                  />
                </div>
              </div>

              {/* Logo / SVG Upload or URL */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-[#C5A46E]" />
                    Лого на сайта (Персонализирано изображение или SVG)
                  </h4>
                  {content.branding.logoUrl && (
                    <button
                      onClick={() => updateSection('branding', { logoUrl: '' })}
                      className="text-[11px] text-red-500 hover:underline font-bold"
                    >
                      Върни стандартния златен SVG герб
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-600 mb-1">
                      URL адрес на лого изображение / SVG или качване:
                    </label>
                    <input
                      type="text"
                      placeholder="https://... или качете локален файл"
                      value={content.branding.logoUrl}
                      onChange={(e) => updateSection('branding', { logoUrl: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#C5A46E]"
                    />
                    
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition">
                        <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>Качи лого от компютър</span>
                        <input
                          type="file"
                          accept="image/*,.svg"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => updateSection('branding', { logoUrl: url }))}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setMediaPicker({
                          isOpen: true,
                          allowedType: 'image',
                          title: 'Изберете лого от качените файлове в public/',
                          onSelect: (url) => updateSection('branding', { logoUrl: url })
                        })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold rounded-lg cursor-pointer transition"
                      >
                        <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>📁 Избери от public</span>
                      </button>
                    </div>
                  </div>

                  <div className="p-3 bg-white border border-slate-200 rounded-xl flex flex-col items-center justify-center text-center h-28">
                    <span className="text-[10px] text-slate-400 font-bold uppercase mb-1">Превю на Лого</span>
                    {content.branding.logoUrl ? (
                      <img 
                        src={content.branding.logoUrl} 
                        alt={content.branding.logoAlt} 
                        className="max-h-16 object-contain"
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-[#0F1E32]">
                        <div className="w-8 h-8 rounded-lg bg-[#C5A46E] flex items-center justify-center text-white font-black text-xs">
                          VIC
                        </div>
                        <span className="text-xs font-black">Стандартно векторно лого</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Favicon (Икона за браузъра) - 1-Click Visual Gallery Picker */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
                  <div>
                    <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#C5A46E]" />
                      Икона на сайта (Favicon)
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Кликнете върху някоя от снимките от галерията по-долу за моментален избор като икона в браузъра.
                    </p>
                  </div>
                  
                  {/* Browser Tab Simulation Preview */}
                  <div className="bg-[#202124] rounded-lg px-3 py-1.5 flex items-center gap-2 border border-slate-700 shadow-md">
                    <img 
                      src={content.branding.faviconUrl || '/imotencenterlogo-cropped.png'} 
                      alt="Favicon" 
                      className="w-4 h-4 object-contain rounded-sm shrink-0 bg-white/10 p-0.5"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/imotencenterlogo-cropped.png';
                      }}
                    />
                    <span className="text-[11px] font-medium text-slate-200 max-w-[130px] truncate">
                      {content.branding.siteTitle || 'Варна Имотен Център'}
                    </span>
                    <span className="text-slate-500 text-[10px] leading-none shrink-0">✕</span>
                  </div>
                </div>

                {/* 1-Click Visual Thumbnail Gallery */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <span>🖼️</span> Изберете от галерията с 1 клик:
                    </span>
                    <span className="text-[11px] text-slate-500 font-medium">
                      Текуща икона: <code className="text-[#C5A46E] bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">{content.branding.faviconUrl || '/imotencenterlogo-cropped.png'}</code>
                    </span>
                  </div>

                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2.5 p-3 bg-white rounded-xl border border-slate-200 max-h-56 overflow-y-auto shadow-inner">
                    {galleryImages.map((img, idx) => {
                      const isSelected = (content.branding.faviconUrl || '/imotencenterlogo-cropped.png') === img;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            updateSection('branding', { faviconUrl: img });
                            showToast('Favicon иконата е избрана успешно!');
                          }}
                          title={img}
                          className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all p-1 flex items-center justify-center cursor-pointer ${
                            isSelected
                              ? 'border-[#C5A46E] ring-2 ring-[#C5A46E]/50 bg-amber-50/50 shadow-md scale-105 z-10'
                              : 'border-slate-200 bg-slate-50 hover:border-[#C5A46E]/60 hover:bg-slate-100'
                          }`}
                        >
                          <img 
                            src={img} 
                            alt={`Gallery ${idx}`}
                            className="w-full h-full object-contain rounded-lg"
                            onError={(e) => {
                              // If broken image, don't crash
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-emerald-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-black shadow">
                              ✓
                            </div>
                          )}
                          <div className="absolute inset-0 bg-[#0F1E32]/0 group-hover:bg-[#0F1E32]/20 transition-colors pointer-events-none rounded-lg" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <label className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#0F1E32] hover:bg-[#1B324E] text-white text-xs font-bold rounded-lg cursor-pointer transition shadow-sm">
                    <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                    <span>Качи файл от компютър</span>
                    <input
                      type="file"
                      accept="image/*,.ico,.svg,.png,.webp"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, (url) => {
                        updateSection('branding', { faviconUrl: url });
                        showToast('Новата икона е качена и зададена като Favicon!');
                      })}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => setMediaPicker({
                      isOpen: true,
                      allowedType: 'image',
                      title: 'Изберете Favicon от медийните файлове в public/',
                      onSelect: (url) => {
                        updateSection('branding', { faviconUrl: url });
                        showToast('Иконата е избрана от медийната библиотека!');
                      }
                    })}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold rounded-lg cursor-pointer transition shadow-sm"
                  >
                    <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                    <span>📁 Отвори цялата медийна библиотека</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      updateSection('branding', { faviconUrl: '/imotencenterlogo-cropped.png' });
                      showToast('Официалното лого е зададено като Favicon!');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-900 border border-amber-300 text-xs font-bold rounded-lg cursor-pointer transition"
                  >
                    <span>⭐ Официално лого на агенцията</span>
                  </button>
                </div>
              </div>

              {/* Accent colors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Основен златен / акцентен цвят (HEX)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={content.branding.accentColor}
                      onChange={(e) => updateSection('branding', { accentColor: e.target.value })}
                      className="w-10 h-10 rounded border border-slate-300 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={content.branding.accentColor}
                      onChange={(e) => updateSection('branding', { accentColor: e.target.value })}
                      className="w-32 bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono uppercase"
                    />
                    <span className="text-xs text-slate-400">Стандартен: #C5A46E</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Тъмен фон на марката (HEX)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={content.branding.darkBgColor}
                      onChange={(e) => updateSection('branding', { darkBgColor: e.target.value })}
                      className="w-10 h-10 rounded border border-slate-300 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={content.branding.darkBgColor}
                      onChange={(e) => updateSection('branding', { darkBgColor: e.target.value })}
                      className="w-32 bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono uppercase"
                    />
                    <span className="text-xs text-slate-400">Стандартен: #0F1E32</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 2. NAVIGATION & TOP BAR ================= */}
          {activeTab === 'navigation' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Горна информационна лента и Главно меню
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Редактирайте контактните данни в заглавната лента, имената на бутоните в главното меню и бутона за действие.
                </p>
              </div>

              {/* Top bar controls */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#C5A46E]" />
                    Информационна лента най-горе (Top Bar)
                  </h4>
                  <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.navigation.topBar.showTopBar}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, showTopBar: e.target.checked }
                      })}
                      className="rounded text-[#C5A46E] focus:ring-[#C5A46E]"
                    />
                    <span>Показвай горната лента</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Адрес за показване</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.addressText}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, addressText: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Телефонен номер</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.phone}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, phone: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Имейл адрес</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.email}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, email: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Facebook линк</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.facebookUrl}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, facebookUrl: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Instagram линк</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.instagramUrl}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, instagramUrl: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">LinkedIn линк</label>
                    <input
                      type="text"
                      value={content.navigation.topBar.linkedinUrl}
                      onChange={(e) => updateSection('navigation', {
                        topBar: { ...content.navigation.topBar, linkedinUrl: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Menu items labels */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Етикети на бутоните в главното меню (Навигация)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(content.navigation.menuLabels).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">
                        Меню: {key}
                      </label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateSection('navigation', {
                          menuLabels: { ...content.navigation.menuLabels, [key]: e.target.value }
                        })}
                        className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Header Action Button */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                    Акцентен бутон в Хедъра (Дясно)
                  </h4>
                  <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.navigation.headerButton.show}
                      onChange={(e) => updateSection('navigation', {
                        headerButton: { ...content.navigation.headerButton, show: e.target.checked }
                      })}
                      className="rounded text-[#C5A46E]"
                    />
                    <span>Показвай бутона</span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Текст на бутона</label>
                  <input
                    type="text"
                    value={content.navigation.headerButton.text}
                    onChange={(e) => updateSection('navigation', {
                      headerButton: { ...content.navigation.headerButton, text: e.target.value }
                    })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 3. HERO & HOMEPAGE ================= */}
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Начална страница и Главен банер (Hero)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Редактирайте големите заглавия, фона, бутоните за действие и баджовете за доверие.
                </p>
              </div>

              {/* Hero Headings */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Малък горен надпис (Badge)
                  </label>
                  <input
                    type="text"
                    value={content.hero.badge}
                    onChange={(e) => updateSection('hero', { badge: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-800"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">
                      Главно Заглавие - Ред 1
                    </label>
                    <input
                      type="text"
                      value={content.hero.titleLine1}
                      onChange={(e) => updateSection('hero', { titleLine1: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-black text-[#0F1E32]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">
                      Главно Заглавие - Ред 2
                    </label>
                    <input
                      type="text"
                      value={content.hero.titleLine2}
                      onChange={(e) => updateSection('hero', { titleLine2: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-black text-[#C5A46E]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Подзаглавие / Описание на Hero
                  </label>
                  <textarea
                    rows={3}
                    value={content.hero.subtitle}
                    onChange={(e) => updateSection('hero', { subtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-700 leading-relaxed"
                  />
                </div>
              </div>

              {/* Hero Background Image */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#C5A46E]" />
                  Фоново изображение на Hero (Голям банер)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2 space-y-2">
                    <input
                      type="text"
                      value={content.hero.bgImage}
                      onChange={(e) => updateSection('hero', { bgImage: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                      placeholder="https://..."
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition">
                        <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>Качи ново изображение</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => updateSection('hero', { bgImage: url }))}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setMediaPicker({
                          isOpen: true,
                          allowedType: 'all',
                          title: 'Изберете фоново изображение или видео за Hero от public/',
                          onSelect: (url) => updateSection('hero', { bgImage: url })
                        })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold rounded-lg cursor-pointer transition"
                      >
                        <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>📁 Избери от public</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateSection('hero', { bgImage: '/Website video.mp4' })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 text-xs font-bold rounded-lg cursor-pointer transition"
                      >
                        <Video className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>🎬 Използвай Website video.mp4</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateSection('hero', { bgImage: STOCK_GALLERY_IMAGES[0] })}
                        className="text-[11px] text-slate-500 hover:text-[#C5A46E] underline font-medium"
                      >
                        Панорама на Варна
                      </button>
                    </div>
                  </div>

                  <div className="h-28 rounded-xl overflow-hidden border border-slate-200 relative bg-slate-900">
                    {isVideoUrl(content.hero.bgImage) ? (
                      <video 
                        key={content.hero.bgImage}
                        src={formatMediaUrl(content.hero.bgImage)} 
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <img 
                        src={content.hero.bgImage} 
                        alt="Hero preview" 
                        className="w-full h-full object-cover" 
                      />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-[10px] font-bold uppercase tracking-wider">
                      {isVideoUrl(content.hero.bgImage) ? '🎬 Видео фон (Hero)' : '🖼️ Превю на фона'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Hero Buttons Text */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Текст на Основен бутон 1 (Продажби)
                  </label>
                  <input
                    type="text"
                    value={content.hero.buttonSalesText}
                    onChange={(e) => updateSection('hero', { buttonSalesText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-[#0F1E32]"
                  />
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Текст на Бутон 2 (Наеми)
                  </label>
                  <input
                    type="text"
                    value={content.hero.buttonRentalsText}
                    onChange={(e) => updateSection('hero', { buttonRentalsText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-slate-700"
                  />
                </div>
              </div>

              {/* Featured Property on Hero Selection */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#C5A46E]" />
                    ⭐ Обява на Херото (Картата вдясно на заглавната страница)
                  </h4>
                  {content.hero.featuredPropertyId && (
                    <button
                      type="button"
                      onClick={() => updateSection('hero', { featuredPropertyId: '' })}
                      className="text-[11px] text-red-600 hover:underline font-bold cursor-pointer"
                    >
                      Нулирай (автоматично първата най-нова)
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Изберете коя точно обява да се визуализира на видно място в Херо секцията вдясно. Ако оставите празно, сайтът показва първата най-нова оферта.
                </p>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1.5 uppercase">
                    Изберете имот за Херото:
                  </label>
                  <select
                    value={content.hero.featuredPropertyId || ''}
                    onChange={(e) => updateSection('hero', { featuredPropertyId: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-[#C5A46E] focus:outline-none cursor-pointer"
                  >
                    <option value="">-- Автоматично (първата най-нова качена оферта) --</option>
                    {liveProperties.map((apt) => (
                      <option key={apt.id} value={apt.id}>
                        {typeof apt.title === 'object' ? (apt.title as any)?.name || 'Имот' : apt.title} (ID: #{apt.id} | {apt.type === 'sale' ? 'Продажба' : 'Наем'} | €{apt.price.toLocaleString('bg-BG')}{apt.neighborhood ? ` | ${typeof apt.neighborhood === 'object' ? (apt.neighborhood as any)?.name : apt.neighborhood}` : ''})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Interactive Badges & Trust Badges */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#C5A46E]" />
                  Трите баджа за сигурност и гаранция
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">Бадж 1</label>
                    <input
                      type="text"
                      value={content.hero.trustBadges.badge1}
                      onChange={(e) => updateSection('hero', {
                        trustBadges: { ...content.hero.trustBadges, badge1: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">Бадж 2</label>
                    <input
                      type="text"
                      value={content.hero.trustBadges.badge2}
                      onChange={(e) => updateSection('hero', {
                        trustBadges: { ...content.hero.trustBadges, badge2: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 mb-1">Бадж 3</label>
                    <input
                      type="text"
                      value={content.hero.trustBadges.badge3}
                      onChange={(e) => updateSection('hero', {
                        trustBadges: { ...content.hero.trustBadges, badge3: e.target.value }
                      })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 4. FEATURED SECTION ================= */}
          {activeTab === 'featured' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Секция „Подбрани оферти“ (Начална страница)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Управлявайте заглавието, подзаглавието и бутона към пълния каталог с имоти.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Заглавие на секцията
                  </label>
                  <input
                    type="text"
                    value={content.featuredSection.title}
                    onChange={(e) => updateSection('featuredSection', { title: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-bold text-[#0F1E32]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Подзаглавие / Описание
                  </label>
                  <input
                    type="text"
                    value={content.featuredSection.subtitle}
                    onChange={(e) => updateSection('featuredSection', { subtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Текст на бутона за преглед на всички оферти
                  </label>
                  <input
                    type="text"
                    value={content.featuredSection.viewAllButtonText}
                    onChange={(e) => updateSection('featuredSection', { viewAllButtonText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs font-bold text-slate-800"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 5. SERVICES ================= */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Секция „Нашите услуги“ (Карти & Текстове)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Редактирайте заглавията и съдържанието на 3-те основни карти с брокерски услуги.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Горен бейдж</label>
                  <input
                    type="text"
                    value={content.servicesSection.badge}
                    onChange={(e) => updateSection('servicesSection', { badge: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие</label>
                  <input
                    type="text"
                    value={content.servicesSection.title}
                    onChange={(e) => updateSection('servicesSection', { title: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Подзаглавие</label>
                <input
                  type="text"
                  value={content.servicesSection.subtitle}
                  onChange={(e) => updateSection('servicesSection', { subtitle: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                />
              </div>

              {/* 3 Teaser Cards */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  3-те Карти с Услуги
                </h4>
                {content.servicesSection.teaserCards.map((card, idx) => (
                  <div key={card.id || idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-[#C5A46E] uppercase tracking-wider">
                        Карта #{idx + 1}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Заглавие на картата</label>
                        <input
                          type="text"
                          value={card.title}
                          onChange={(e) => {
                            const newCards = [...content.servicesSection.teaserCards];
                            newCards[idx] = { ...newCards[idx], title: e.target.value };
                            updateSection('servicesSection', { teaserCards: newCards });
                          }}
                          className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Текст на бутона</label>
                        <input
                          type="text"
                          value={card.buttonText}
                          onChange={(e) => {
                            const newCards = [...content.servicesSection.teaserCards];
                            newCards[idx] = { ...newCards[idx], buttonText: e.target.value };
                            updateSection('servicesSection', { teaserCards: newCards });
                          }}
                          className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-amber-700"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Описание на услугата</label>
                      <textarea
                        rows={2}
                        value={card.description}
                        onChange={(e) => {
                          const newCards = [...content.servicesSection.teaserCards];
                          newCards[idx] = { ...newCards[idx], description: e.target.value };
                          updateSection('servicesSection', { teaserCards: newCards });
                        }}
                        className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-700"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Full Detailed Services Page Cards (Услуги - Страница) */}
              <div className="pt-6 border-t border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-extrabold text-[#0F1E32] uppercase tracking-wider">
                      Големи карти в секция „Услуги“ (Страница Услуги)
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Редактирайте подробните карти с услуги, техните заглавия, описания и списъци с предимства / действия.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const currentList = content.servicesPage?.servicesList || [];
                      const newService = {
                        id: `sec-${Date.now()}`,
                        title: 'НОВА УСЛУГА',
                        description: 'Описание на новата професионална услуга на агенцията.',
                        icon: 'Handshake',
                        details: ['Първа точка от услугата', 'Втора точка от услугата']
                      };
                      updateSection('servicesPage', {
                        servicesList: [...currentList, newService]
                      });
                      showToast('Добавена е нова карта за услуга!');
                    }}
                    className="px-3.5 py-1.5 bg-[#0F1E32] hover:bg-[#1a2f4c] text-white text-xs font-bold rounded-lg shadow-sm transition flex items-center gap-1.5"
                  >
                    + Добави услуга
                  </button>
                </div>

                {(content.servicesPage?.servicesList || []).map((service, sIdx) => (
                  <div key={service.id || sIdx} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">
                          {sIdx + 1}
                        </span>
                        <span className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                          {service.title || `Услуга #${sIdx + 1}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const currentList = [...(content.servicesPage?.servicesList || [])];
                          currentList.splice(sIdx, 1);
                          updateSection('servicesPage', { servicesList: currentList });
                          showToast('Услугата беше изтрита');
                        }}
                        className="text-red-500 hover:text-red-700 text-xs font-bold p-1 hover:bg-red-50 rounded transition"
                        title="Изтрий услуга"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Заглавие на услугата</label>
                        <input
                          type="text"
                          value={service.title}
                          onChange={(e) => {
                            const currentList = [...(content.servicesPage?.servicesList || [])];
                            currentList[sIdx] = { ...currentList[sIdx], title: e.target.value };
                            updateSection('servicesPage', { servicesList: currentList });
                          }}
                          className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-[#0F1E32]"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Икона</label>
                        <select
                          value={service.icon}
                          onChange={(e) => {
                            const currentList = [...(content.servicesPage?.servicesList || [])];
                            currentList[sIdx] = { ...currentList[sIdx], icon: e.target.value };
                            updateSection('servicesPage', { servicesList: currentList });
                          }}
                          className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-semibold"
                        >
                          <option value="Handshake">🤝 Ръкостискане (Handshake)</option>
                          <option value="FileText">📄 Документи (FileText)</option>
                          <option value="TrendingUp">📈 Анализи / Ръст (TrendingUp)</option>
                          <option value="Heart">❤️ С грижа (Heart)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Описание</label>
                      <textarea
                        rows={2}
                        value={service.description}
                        onChange={(e) => {
                          const currentList = [...(content.servicesPage?.servicesList || [])];
                          currentList[sIdx] = { ...currentList[sIdx], description: e.target.value };
                          updateSection('servicesPage', { servicesList: currentList });
                        }}
                        className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-700 leading-relaxed"
                      />
                    </div>

                    {/* Checklist points */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[11px] font-bold text-slate-600">Списък с предимства / действия (чеклист):</label>
                        <button
                          type="button"
                          onClick={() => {
                            const currentList = [...(content.servicesPage?.servicesList || [])];
                            const currentDetails = currentList[sIdx].details || [];
                            currentList[sIdx] = {
                              ...currentList[sIdx],
                              details: [...currentDetails, 'Ново действие или характеристика']
                            };
                            updateSection('servicesPage', { servicesList: currentList });
                          }}
                          className="text-[#966D29] hover:underline text-[11px] font-bold"
                        >
                          + Добави точка
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {(service.details || []).map((det, dIdx) => (
                          <div key={dIdx} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={det}
                              onChange={(e) => {
                                const currentList = [...(content.servicesPage?.servicesList || [])];
                                const currentDetails = [...(currentList[sIdx].details || [])];
                                currentDetails[dIdx] = e.target.value;
                                currentList[sIdx] = { ...currentList[sIdx], details: currentDetails };
                                updateSection('servicesPage', { servicesList: currentList });
                              }}
                              className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 text-xs"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const currentList = [...(content.servicesPage?.servicesList || [])];
                                const currentDetails = [...(currentList[sIdx].details || [])];
                                currentDetails.splice(dIdx, 1);
                                currentList[sIdx] = { ...currentList[sIdx], details: currentDetails };
                                updateSection('servicesPage', { servicesList: currentList });
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Премахни точка"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ================= 6. ABOUT & MISSION ================= */}
          {activeTab === 'about' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Секция „За нас / Нашата история и мисия“
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Управлявайте представителния текст за агенцията, снимката и бутона за детайли.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Горен бейдж</label>
                  <input
                    type="text"
                    value={content.aboutSection.badge}
                    onChange={(e) => updateSection('aboutSection', { badge: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие</label>
                  <input
                    type="text"
                    value={content.aboutSection.title}
                    onChange={(e) => updateSection('aboutSection', { title: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-[#0F1E32]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Параграф 1 (Основна визия)</label>
                <textarea
                  rows={3}
                  value={content.aboutSection.paragraph1}
                  onChange={(e) => updateSection('aboutSection', { paragraph1: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-700"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Параграф 2 (Опит и подход)</label>
                <textarea
                  rows={3}
                  value={content.aboutSection.paragraph2}
                  onChange={(e) => updateSection('aboutSection', { paragraph2: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-700"
                />
              </div>

              {/* About image */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#C5A46E]" />
                  Представителна снимка на агенцията
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2 space-y-2">
                    <input
                      type="text"
                      value={content.aboutSection.image}
                      onChange={(e) => updateSection('aboutSection', { image: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                      placeholder="https://..."
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition">
                        <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>Качи снимка на агенцията</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e, (url) => updateSection('aboutSection', { image: url }))}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setMediaPicker({
                          isOpen: true,
                          allowedType: 'image',
                          title: 'Изберете представителна снимка от public/',
                          onSelect: (url) => updateSection('aboutSection', { image: url })
                        })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold rounded-lg cursor-pointer transition"
                      >
                        <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>📁 Избери от public</span>
                      </button>
                    </div>
                  </div>
                  <div className="h-28 rounded-xl overflow-hidden border border-slate-200 relative">
                    <img 
                      src={content.aboutSection.image} 
                      alt="About preview" 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  Текст на бутона за детайли
                </label>
                <input
                  type="text"
                  value={content.aboutSection.buttonText}
                  onChange={(e) => updateSection('aboutSection', { buttonText: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-slate-800"
                />
              </div>

              {/* ================= GOOGLE REVIEWS SCREENSHOTS CAROUSEL MANAGER ================= */}
              <div className="pt-8 mt-8 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h4 className="font-extrabold text-[#0F1E32] text-sm flex items-center gap-2">
                      <span>📸 Изрезки от Google отзиви (Карусел)</span>
                      <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full">
                        {content.aboutSection.googleReviewsScreenshots?.length || 0} снимки
                      </span>
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Качвайте скрийншоти от отзиви в Google (директно от папка <code className="font-mono text-[#0F1E32] bg-slate-100 px-1 rounded">public/</code> или от устройството). Те се въртят автоматично в секция „За нас“.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMediaPicker({
                        isOpen: true,
                        allowedType: 'image',
                        title: 'Изберете изрезка от отзив от public/',
                        onSelect: (url) => {
                          const currentList = content.aboutSection.googleReviewsScreenshots || [];
                          const newItem = {
                            id: `rev-${Date.now()}`,
                            imageUrl: url,
                            caption: 'Отзив от клиент в Google Maps'
                          };
                          updateSection('aboutSection', {
                            googleReviewsScreenshots: [...currentList, newItem]
                          });
                          showToast('Изрезката от отзив беше добавена в карусела!');
                        }
                      })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold rounded-lg cursor-pointer transition shadow-sm"
                    >
                      <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>📁 Избери от public</span>
                    </button>

                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition shadow-sm">
                      <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>Качи нов скрийншот</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, (url) => {
                          const currentList = content.aboutSection.googleReviewsScreenshots || [];
                          const newItem = {
                            id: `rev-${Date.now()}`,
                            imageUrl: url,
                            caption: 'Отзив от клиент в Google'
                          };
                          updateSection('aboutSection', {
                            googleReviewsScreenshots: [...currentList, newItem]
                          });
                          showToast('Новият скрийншот беше качен успешно!');
                        })}
                      />
                    </label>
                  </div>
                </div>

                {/* Carousel Headings & Rating Controls */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5 bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4">
                  {/* Rating value */}
                  <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-xs">
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 flex items-center justify-between">
                      <span>Оценка в звезди</span>
                      <span className="text-amber-500 font-black text-xs">
                        ★ {Number(content.aboutSection.reviewsRating !== undefined ? content.aboutSection.reviewsRating : 5.0).toFixed(1)}
                      </span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="1.0"
                      max="5.0"
                      value={content.aboutSection.reviewsRating !== undefined ? content.aboutSection.reviewsRating : 5.0}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        updateSection('aboutSection', { reviewsRating: isNaN(val) ? 5.0 : Math.min(5, Math.max(1, Math.round(val * 10) / 10)) });
                      }}
                      className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 text-xs font-black text-slate-800"
                    />
                    {/* Quick presets */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {[5.0, 4.9, 4.8, 4.7, 4.5].map((preset) => {
                        const current = Number(content.aboutSection.reviewsRating !== undefined ? content.aboutSection.reviewsRating : 5.0);
                        const isSelected = Math.abs(current - preset) < 0.05;
                        return (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => updateSection('aboutSection', { reviewsRating: preset })}
                            className={`px-1.5 py-0.5 text-[10px] rounded font-bold transition cursor-pointer ${
                              isSelected
                                ? 'bg-amber-500 text-slate-950 shadow-xs'
                                : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                            }`}
                          >
                            {preset}★
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Rating display text */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Текст за рейтинга
                    </label>
                    <input
                      type="text"
                      placeholder={`${Number(content.aboutSection.reviewsRating !== undefined ? content.aboutSection.reviewsRating : 5.0).toFixed(1)} Рейтинг в Google`}
                      value={content.aboutSection.reviewsRatingText || ''}
                      onChange={(e) => updateSection('aboutSection', { reviewsRatingText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-800"
                    />
                    <span className="text-[10px] text-slate-400 mt-1 block">
                      Празно = автоматично от оценката
                    </span>
                  </div>

                  {/* Section Title */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Заглавие на секцията
                    </label>
                    <input
                      type="text"
                      value={content.aboutSection.reviewsTitle || 'Какво споделят клиентите за нас'}
                      onChange={(e) => updateSection('aboutSection', { reviewsTitle: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-slate-800"
                    />
                  </div>

                  {/* Section Subtitle */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Подзаглавие
                    </label>
                    <input
                      type="text"
                      value={content.aboutSection.reviewsSubtitle || 'Скрийншоти и оценки от доволни купувачи, продавачи и наематели във Варна'}
                      onChange={(e) => updateSection('aboutSection', { reviewsSubtitle: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-slate-800"
                    />
                  </div>

                  {/* Google Profile Link */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Линк за бутона „Виж в Google“
                    </label>
                    <input
                      type="url"
                      placeholder="https://maps.google.com/..."
                      value={content.aboutSection.googleReviewsUrl || ''}
                      onChange={(e) => updateSection('aboutSection', { googleReviewsUrl: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono text-[#0F1E32]"
                    />
                  </div>
                </div>

                {/* Screenshots List */}
                <div className="space-y-3">
                  {(content.aboutSection.googleReviewsScreenshots || []).length === 0 ? (
                    <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl text-center">
                      <p className="text-xs text-slate-500 font-medium">
                        Все още няма качени скрийншоти. Натиснете бутона <strong>„📁 Избери от public“</strong> или <strong>„Качи нов скрийншот“</strong> по-горе, за да добавите първия скрийншот от отзив.
                      </p>
                    </div>
                  ) : (
                    (content.aboutSection.googleReviewsScreenshots || []).map((rev, index) => (
                      <div key={rev.id || index} className="p-3 bg-white border border-slate-200 rounded-xl flex flex-col sm:flex-row items-center gap-3 shadow-sm hover:border-slate-300 transition">
                        <div className="w-28 h-20 bg-slate-100 rounded-lg overflow-hidden border border-slate-200 shrink-0 relative group">
                          <img
                            src={rev.imageUrl}
                            alt=""
                            className="w-full h-full object-contain bg-slate-900"
                          />
                        </div>

                        <div className="flex-1 w-full space-y-1.5 text-left">
                          <input
                            type="text"
                            placeholder="Описание на отзива (напр. Покупка на апартамент в кв. Чайка)"
                            value={rev.caption || ''}
                            onChange={(e) => {
                              const updated = [...(content.aboutSection.googleReviewsScreenshots || [])];
                              updated[index] = { ...updated[index], caption: e.target.value };
                              updateSection('aboutSection', { googleReviewsScreenshots: updated });
                            }}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-medium"
                          />
                          <div className="text-[11px] text-slate-400 font-mono truncate">
                            Файл: {rev.imageUrl}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setMediaPicker({
                              isOpen: true,
                              allowedType: 'image',
                              title: 'Смени снимката от public/',
                              onSelect: (url) => {
                                const updated = [...(content.aboutSection.googleReviewsScreenshots || [])];
                                updated[index] = { ...updated[index], imageUrl: url };
                                updateSection('aboutSection', { googleReviewsScreenshots: updated });
                              }
                            })}
                            className="p-1.5 text-slate-500 hover:text-[#0F1E32] hover:bg-slate-100 rounded-lg transition text-xs font-bold"
                            title="Смени снимката от public"
                          >
                            📁 Смени
                          </button>

                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => {
                              if (index === 0) return;
                              const updated = [...(content.aboutSection.googleReviewsScreenshots || [])];
                              const temp = updated[index - 1];
                              updated[index - 1] = updated[index];
                              updated[index] = temp;
                              updateSection('aboutSection', { googleReviewsScreenshots: updated });
                            }}
                            className="p-1.5 text-slate-500 hover:text-slate-800 disabled:opacity-30 rounded hover:bg-slate-100 text-xs font-bold"
                            title="Премести нагоре"
                          >
                            ↑
                          </button>

                          <button
                            type="button"
                            disabled={index === (content.aboutSection.googleReviewsScreenshots || []).length - 1}
                            onClick={() => {
                              const currentList = content.aboutSection.googleReviewsScreenshots || [];
                              if (index >= currentList.length - 1) return;
                              const updated = [...currentList];
                              const temp = updated[index + 1];
                              updated[index + 1] = updated[index];
                              updated[index] = temp;
                              updateSection('aboutSection', { googleReviewsScreenshots: updated });
                            }}
                            className="p-1.5 text-slate-500 hover:text-slate-800 disabled:opacity-30 rounded hover:bg-slate-100 text-xs font-bold"
                            title="Премести надолу"
                          >
                            ↓
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              const updated = (content.aboutSection.googleReviewsScreenshots || []).filter((_, i) => i !== index);
                              updateSection('aboutSection', { googleReviewsScreenshots: updated });
                              showToast('Изрезката беше премахната');
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Изтрий от карусела"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ================= 7. TEAM TEASER ================= */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Секция „Запознайте се с нашия екип“
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Управлявайте представянето на екипа на началната страница и бутона за преглед на всички брокери.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Горен бейдж</label>
                  <input
                    type="text"
                    value={content.teamSection.badge}
                    onChange={(e) => updateSection('teamSection', { badge: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие</label>
                  <input
                    type="text"
                    value={content.teamSection.title}
                    onChange={(e) => updateSection('teamSection', { title: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-[#0F1E32]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Подзаглавие</label>
                  <input
                    type="text"
                    value={content.teamSection.subtitle}
                    onChange={(e) => updateSection('teamSection', { subtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Текст на бутона „Виж целия екип“
                  </label>
                  <input
                    type="text"
                    value={content.teamSection.viewAllButtonText}
                    onChange={(e) => updateSection('teamSection', { viewAllButtonText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 8. CTA BANNER ================= */}
          {activeTab === 'cta' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Банер за призив за действие (CTA Banner)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Тъмният широк банер с бутони за обаждане и запитване, който насърчава посетителите към контакт.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие (Начало)</label>
                  <input
                    type="text"
                    value={content.ctaBanner.titlePrefix}
                    onChange={(e) => updateSection('ctaBanner', { titlePrefix: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Оцветена златна дума</label>
                  <input
                    type="text"
                    value={content.ctaBanner.highlightedText}
                    onChange={(e) => updateSection('ctaBanner', { highlightedText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold text-[#C5A46E]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие (Край)</label>
                  <input
                    type="text"
                    value={content.ctaBanner.titleSuffix}
                    onChange={(e) => updateSection('ctaBanner', { titleSuffix: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Подзаглавие</label>
                <textarea
                  rows={2}
                  value={content.ctaBanner.subtitle}
                  onChange={(e) => updateSection('ctaBanner', { subtitle: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-700"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Текст на бутона за телефон</label>
                  <input
                    type="text"
                    value={content.ctaBanner.phoneButtonText}
                    onChange={(e) => updateSection('ctaBanner', { phoneButtonText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Текст на бутона за запитване</label>
                  <input
                    type="text"
                    value={content.ctaBanner.contactButtonText}
                    onChange={(e) => updateSection('ctaBanner', { contactButtonText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
              </div>

              {/* Background image */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                <label className="block text-xs font-bold text-slate-600 mb-1">Фоново изображение на CTA банера</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={content.ctaBanner.bgImage}
                    onChange={(e) => updateSection('ctaBanner', { bgImage: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    placeholder="Път в public или URL..."
                  />
                  <div className="flex items-center gap-2 shrink-0">
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition">
                      <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>Качи</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, (url) => updateSection('ctaBanner', { bgImage: url }))}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setMediaPicker({
                        isOpen: true,
                        allowedType: 'all',
                        title: 'Изберете фоново изображение или видео за CTA банера от public/',
                        onSelect: (url) => updateSection('ctaBanner', { bgImage: url })
                      })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold rounded-lg cursor-pointer transition"
                    >
                      <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>📁 Избери от public</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 9. SALES & RENTALS PAGES ================= */}
          {activeTab === 'pages' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Страници „Продажби“ и „Наеми“
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Заглавия, подзаглавия и 4-те стъпки в ръководството „Как работим при наемане“.
                </p>
              </div>

              {/* Sales Page */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Страница „Имоти за продажба“
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Горен бейдж</label>
                    <input
                      type="text"
                      value={content.salesPage.badge}
                      onChange={(e) => updateSection('salesPage', { badge: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Заглавие</label>
                    <input
                      type="text"
                      value={content.salesPage.title}
                      onChange={(e) => updateSection('salesPage', { title: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Подзаглавие</label>
                  <input
                    type="text"
                    value={content.salesPage.subtitle}
                    onChange={(e) => updateSection('salesPage', { subtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>
              </div>

              {/* Rentals Page */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Страница „Имоти под наем“
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Горен бейдж</label>
                    <input
                      type="text"
                      value={content.rentalsPage.badge}
                      onChange={(e) => updateSection('rentalsPage', { badge: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Заглавие</label>
                    <input
                      type="text"
                      value={content.rentalsPage.title}
                      onChange={(e) => updateSection('rentalsPage', { title: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Подзаглавие</label>
                  <input
                    type="text"
                    value={content.rentalsPage.subtitle}
                    onChange={(e) => updateSection('rentalsPage', { subtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>

                {/* 4 Steps */}
                <div className="pt-2">
                  <h5 className="text-xs font-bold text-[#0F1E32] mb-3 uppercase">
                    4-те стъпки в „Как работим при наемане“
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {content.rentalsPage.steps.map((step, idx) => (
                      <div key={idx} className="p-3 bg-white border border-slate-200 rounded-xl space-y-2">
                        <span className="text-xs font-black text-[#C5A46E]">Стъпка {step.number}</span>
                        <input
                          type="text"
                          value={step.title}
                          onChange={(e) => {
                            const newSteps = [...content.rentalsPage.steps];
                            newSteps[idx] = { ...newSteps[idx], title: e.target.value };
                            updateSection('rentalsPage', { steps: newSteps });
                          }}
                          className="w-full bg-slate-50 border border-slate-300 rounded p-1.5 text-xs font-bold"
                        />
                        <textarea
                          rows={2}
                          value={step.description}
                          onChange={(e) => {
                            const newSteps = [...content.rentalsPage.steps];
                            newSteps[idx] = { ...newSteps[idx], description: e.target.value };
                            updateSection('rentalsPage', { steps: newSteps });
                          }}
                          className="w-full bg-slate-50 border border-slate-300 rounded p-1.5 text-xs text-slate-600"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Request Banner */}
                <div className="pt-2 border-t border-slate-200">
                  <h5 className="text-xs font-bold text-[#0F1E32] mb-2 uppercase">
                    Банер за запитване при липса на оферта („Не откривате оферта?“)
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-1">Заглавие</label>
                      <input
                        type="text"
                        value={content.rentalsPage.requestBanner.title}
                        onChange={(e) => updateSection('rentalsPage', {
                          requestBanner: { ...content.rentalsPage.requestBanner, title: e.target.value }
                        })}
                        className="w-full bg-white border border-slate-300 rounded p-2 text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-1">Подзаглавие</label>
                      <input
                        type="text"
                        value={content.rentalsPage.requestBanner.subtitle}
                        onChange={(e) => updateSection('rentalsPage', {
                          requestBanner: { ...content.rentalsPage.requestBanner, subtitle: e.target.value }
                        })}
                        className="w-full bg-white border border-slate-300 rounded p-2 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-1">Текст на бутона</label>
                      <input
                        type="text"
                        value={content.rentalsPage.requestBanner.buttonText}
                        onChange={(e) => updateSection('rentalsPage', {
                          requestBanner: { ...content.rentalsPage.requestBanner, buttonText: e.target.value }
                        })}
                        className="w-full bg-white border border-slate-300 rounded p-2 text-xs font-bold text-amber-700"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 10. CONTACTS PAGE ================= */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Страница „Контакти“ & Форма за запитвания
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Редактирайте адреса на офиса, телефоните, работното време и текстовете на контактната форма.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Име на фирмата в карето</label>
                  <input
                    type="text"
                    value={content.contactsPage.companyName}
                    onChange={(e) => updateSection('contactsPage', { companyName: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Адрес на офиса</label>
                  <input
                    type="text"
                    value={content.contactsPage.address}
                    onChange={(e) => updateSection('contactsPage', { address: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Телефонен номер 1</label>
                  <input
                    type="text"
                    value={content.contactsPage.phone1}
                    onChange={(e) => updateSection('contactsPage', { phone1: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Телефонен номер 2</label>
                  <input
                    type="text"
                    value={content.contactsPage.phone2}
                    onChange={(e) => updateSection('contactsPage', { phone2: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Имейл за запитвания</label>
                  <input
                    type="text"
                    value={content.contactsPage.email}
                    onChange={(e) => updateSection('contactsPage', { email: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Работно време
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Делнични дни (Пон – Пет)</label>
                    <input
                      type="text"
                      value={content.contactsPage.workingHoursWeekdays}
                      onChange={(e) => updateSection('contactsPage', { workingHoursWeekdays: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Събота</label>
                    <input
                      type="text"
                      value={content.contactsPage.workingHoursSaturday}
                      onChange={(e) => updateSection('contactsPage', { workingHoursSaturday: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Неделя</label>
                    <input
                      type="text"
                      value={content.contactsPage.workingHoursSunday}
                      onChange={(e) => updateSection('contactsPage', { workingHoursSunday: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded p-2 text-xs text-red-600 font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Form Controls */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Контактна форма (Заглавие & Бутон)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Заглавие на формата</label>
                    <input
                      type="text"
                      value={content.contactsPage.formTitle}
                      onChange={(e) => updateSection('contactsPage', { formTitle: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded p-2 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-1">Текст на бутона за изпращане</label>
                    <input
                      type="text"
                      value={content.contactsPage.formButtonText}
                      onChange={(e) => updateSection('contactsPage', { formButtonText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded p-2 text-xs font-bold text-amber-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">Подзаглавие на формата</label>
                  <input
                    type="text"
                    value={content.contactsPage.formSubtitle}
                    onChange={(e) => updateSection('contactsPage', { formSubtitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded p-2 text-xs"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ================= 10.5 POPULAR LOCATIONS ================= */}
          {activeTab === 'locations' && (
            <div className="animate-fade-in">
              {renderPopularLocationsManager()}
            </div>
          )}

          {/* ================= 11. FOOTER ================= */}
          {activeTab === 'footer' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Футър (Footer) — Пълно управление
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Управлявайте секцията „За фирмата“, „Популярни локации“, „Нашите услуги“, „Бързи връзки“, контакти и авторски права.
                </p>
              </div>

              {/* Footer About & Contact Info */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Текст за агенцията (Колона 1 във футъра)
                  </label>
                  <textarea
                    rows={3}
                    value={content.footer.aboutText}
                    onChange={(e) => updateSection('footer', { aboutText: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-700"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Адрес във футъра</label>
                    <input
                      type="text"
                      value={content.footer.address}
                      onChange={(e) => updateSection('footer', { address: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Телефон във футъра</label>
                    <input
                      type="text"
                      value={content.footer.phone}
                      onChange={(e) => updateSection('footer', { phone: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Имейл във футъра</label>
                    <input
                      type="text"
                      value={content.footer.email}
                      onChange={(e) => updateSection('footer', { email: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Footer Quick Links Manager */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-[#C5A46E]" />
                      Бързи връзки (Колона във Футъра)
                    </h4>
                    <p className="text-[11px] text-slate-500">
                      Управлявайте основните навигационни връзки във футъра.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newLink = {
                        id: `ql-${Date.now()}`,
                        label: 'Нова връзка',
                        tab: 'home'
                      };
                      updateSection('footer', {
                        quickLinks: [...(content.footer.quickLinks || []), newLink]
                      });
                    }}
                    className="px-3 py-1.5 bg-[#C5A46E] hover:bg-[#B1905E] text-white rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Добави връзка
                  </button>
                </div>

                <div>
                  <label className="block text-xs text-slate-600 mb-1">Заглавие на колоната</label>
                  <input
                    type="text"
                    value={content.footer.quickLinksTitle || 'БЪРЗИ ВРЪЗКИ'}
                    onChange={(e) => updateSection('footer', { quickLinksTitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>

                <div className="space-y-2 mt-3">
                  {(content.footer.quickLinks || []).map((link, idx) => (
                    <div key={link.id || idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => {
                          const updated = [...(content.footer.quickLinks || [])];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          updateSection('footer', { quickLinks: updated });
                        }}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 text-xs font-medium"
                        placeholder="Текст на линка"
                      />
                      <select
                        value={link.tab}
                        onChange={(e) => {
                          const updated = [...(content.footer.quickLinks || [])];
                          updated[idx] = { ...updated[idx], tab: e.target.value };
                          updateSection('footer', { quickLinks: updated });
                        }}
                        className="w-40 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 text-xs font-semibold"
                      >
                        <option value="home">Начало</option>
                        <option value="sales">Продажби</option>
                        <option value="rentals">Наеми</option>
                        <option value="services">Услуги</option>
                        <option value="team">Екип</option>
                        <option value="contacts">Контакти</option>
                        <option value="about">За нас</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (content.footer.quickLinks || []).filter((_, i) => i !== idx);
                          updateSection('footer', { quickLinks: updated });
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded transition cursor-pointer"
                        title="Премахни връзка"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Popular Locations Manager with real-time property counts & dropdown */}
              <div className="bg-slate-50/70 p-4 sm:p-5 rounded-2xl border border-slate-200">
                {renderPopularLocationsManager()}
              </div>

              {/* Footer Services List Manager */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#C5A46E]" />
                      Нашите услуги (Списък във Футъра)
                    </h4>
                  </div>
                  <button
                    onClick={() => {
                      const newServ = {
                        id: `sl-${Date.now()}`,
                        title: 'Нова услуга'
                      };
                      updateSection('footer', {
                        servicesList: [...content.footer.servicesList, newServ]
                      });
                    }}
                    className="px-3 py-1.5 bg-[#C5A46E] hover:bg-[#B1905E] text-white rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Добави услуга
                  </button>
                </div>

                <div>
                  <label className="block text-xs text-slate-600 mb-1">Заглавие на колоната</label>
                  <input
                    type="text"
                    value={content.footer.servicesTitle}
                    onChange={(e) => updateSection('footer', { servicesTitle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                  />
                </div>

                <div className="space-y-2 mt-3">
                  {content.footer.servicesList.map((serv, idx) => (
                    <div key={serv.id || idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                      <input
                        type="text"
                        value={serv.title}
                        onChange={(e) => {
                          const updated = [...content.footer.servicesList];
                          updated[idx] = { ...updated[idx], title: e.target.value };
                          updateSection('footer', { servicesList: updated });
                        }}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 text-xs font-medium"
                      />
                      <button
                        onClick={() => {
                          const updated = content.footer.servicesList.filter((_, i) => i !== idx);
                          updateSection('footer', { servicesList: updated });
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded transition cursor-pointer"
                        title="Премахни услуга"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Google Maps Office Section in Footer */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#C5A46E]" />
                    <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                      Интерактивна Google Maps карта във футъра
                    </h4>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.footer.mapSection?.enabled !== false}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            enabled: e.target.checked
                          }
                        });
                      }}
                      className="w-4 h-4 text-[#C5A46E] rounded border-slate-300 focus:ring-[#C5A46E]"
                    />
                    <span className="text-xs font-semibold text-slate-700">Показвай картата във футъра</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Заглавие</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.title ?? 'Централен офис Варна'}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            title: e.target.value
                          }
                        });
                      }}
                      placeholder="Централен офис Варна"
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Горен бейдж</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.badge ?? 'ОФИС И ЛОКАЦИЯ'}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            badge: e.target.value
                          }
                        });
                      }}
                      placeholder="ОФИС И ЛОКАЦИЯ"
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Подзаглавие / Текст</label>
                  <input
                    type="text"
                    value={content.footer.mapSection?.subtitle ?? 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.'}
                    onChange={(e) => {
                      const current = content.footer.mapSection || {
                        badge: 'ОФИС И ЛОКАЦИЯ',
                        title: 'Централен офис Варна',
                        subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                        address: content.footer.address || '',
                        landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                        workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                        phone: content.footer.phone || '',
                        enabled: true
                      };
                      updateSection('footer', {
                        mapSection: {
                          ...current,
                          subtitle: e.target.value
                        }
                      });
                    }}
                    placeholder="Заповядайте на място за лична консултация..."
                    className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Точен адрес на офиса (за Google Maps)</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.address ?? content.footer.address}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            address: e.target.value
                          }
                        });
                      }}
                      placeholder="гр. Варна, бул. Генерал Колев, 113, ет.3, офис 3.5"
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Ориентир / забележка за локацията</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.landmark ?? 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)'}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            landmark: e.target.value
                          }
                        });
                      }}
                      placeholder="В непосредствена близост до Окръжна болница"
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Работно време</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.workingHours ?? 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.'}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            workingHours: e.target.value
                          }
                        });
                      }}
                      placeholder="Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч."
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Директен телефон</label>
                    <input
                      type="text"
                      value={content.footer.mapSection?.phone ?? content.footer.phone}
                      onChange={(e) => {
                        const current = content.footer.mapSection || {
                          badge: 'ОФИС И ЛОКАЦИЯ',
                          title: 'Централен офис Варна',
                          subtitle: 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.',
                          address: content.footer.address || '',
                          landmark: 'В непосредствена близост до Окръжна болница (бул. Генерал Колев)',
                          workingHours: 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.',
                          phone: content.footer.phone || '',
                          enabled: true
                        };
                        updateSection('footer', {
                          mapSection: {
                            ...current,
                            phone: e.target.value
                          }
                        });
                      }}
                      placeholder="+359 878 859 385"
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Quick links & Copyright */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3">
                <h4 className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Авторски права и правни текстове най-долу
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Копирайт текст (© ...)</label>
                    <input
                      type="text"
                      value={content.footer.copyrightText}
                      onChange={(e) => updateSection('footer', { copyrightText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Текст за лиценз / агенция</label>
                    <input
                      type="text"
                      value={content.footer.creditsText}
                      onChange={(e) => updateSection('footer', { creditsText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Политика за поверителност (текст)</label>
                    <input
                      type="text"
                      value={content.footer.privacyText}
                      onChange={(e) => updateSection('footer', { privacyText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Общи условия (текст)</label>
                    <input
                      type="text"
                      value={content.footer.termsText}
                      onChange={(e) => updateSection('footer', { termsText: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 12. BACKUP & JSON ================= */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-extrabold text-[#0F1E32] uppercase tracking-wide">
                  Архивиране на съдържанието
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Можете да изтеглите пълен резервен файл на всички текстове, заглавия и конфигурации на сайта.
                </p>
              </div>

              <div className="max-w-xl">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 flex flex-col justify-between">
                  <div>
                    <Download className="w-8 h-8 text-[#C5A46E] mb-3" />
                    <h4 className="font-extrabold text-sm text-[#0F1E32] uppercase">Експорт на конфигурация (JSON)</h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Изтегля целия сайт във файл с всички заглавия, текстове, снимки и настройки на шрифтовете.
                    </p>
                  </div>
                  <button
                    onClick={handleExportJSON}
                    className="mt-6 w-full py-2.5 bg-[#0F1E32] hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition flex items-center justify-center gap-2 cursor-pointer shadow"
                  >
                    <Download className="w-4 h-4" />
                    Изтегли JSON Архив
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================= 13. SEO MANAGEMENT ================= */}
          {activeTab === 'seo' && (
            <div className="animate-fade-in">
              <SEOManager />
            </div>
          )}

        </div>
      </div>

      {/* Media Picker Modal from public/ */}
      <MediaPickerModal
        isOpen={mediaPicker.isOpen}
        onClose={() => setMediaPicker(prev => ({ ...prev, isOpen: false }))}
        onSelect={mediaPicker.onSelect}
        allowedType={mediaPicker.allowedType}
        title={mediaPicker.title}
      />
    </div>
  );
}
